/**
 * TsmItemsReportModal
 *
 * Multi-location modal for the TSM Items Report. Always queries all 8
 * service-bearing locations; user picks month/year only. Mirrors the
 * TsmRankingReportModal pattern.
 */

import React, { useMemo, useState } from 'react';
import { XMarkIcon, PlayIcon, DocumentChartBarIcon } from '@heroicons/react/24/outline';
import { ReportDefinition } from '../../types/reports';
import { ReportExportButtons } from './ReportExportButtons';
import { printCurrentPage } from '../../utils/reportExport';
import { loadExcelJS } from '../../utils/loadExcelJS';
import { runWholesaleComparison, RunReportResponse } from '../../services/api';
import {
  TsmItemsReport,
  TsmItemsReportData,
  TsmItemsGroupSection,
  ItemKey,
} from './renderers/TsmItemsReport';

const ELIGIBLE_LOCATION_IDS = [1, 2, 3, 4, 5, 10, 11, 14];

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

interface TsmItemsReportModalProps {
  report: ReportDefinition;
  isOpen: boolean;
  onClose: () => void;
}

type ReportStatus = 'idle' | 'running' | 'complete' | 'error';

export const TsmItemsReportModal: React.FC<TsmItemsReportModalProps> = ({
  report,
  isOpen,
  onClose,
}) => {
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());

  const [status, setStatus] = useState<ReportStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [reportData, setReportData] = useState<RunReportResponse | null>(null);
  const [runningMessage, setRunningMessage] = useState('Generating report...');

  const yearOptions = useMemo(() => {
    const years: number[] = [];
    for (let y = now.getFullYear(); y >= 2026; y--) years.push(y);
    return years;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isRetryableError = (err: unknown): boolean => {
    const msg = err instanceof Error ? err.message.toLowerCase() : '';
    return msg.includes('failed to fetch') || msg.includes('networkerror') ||
      msg.includes('timeout') || msg.includes('504') || msg.includes('503') ||
      msg.includes('network') || msg.includes('aborted') || msg.includes('timed out');
  };

  const handleRunReport = async () => {
    setStatus('running');
    setError(null);
    setReportData(null);
    setRunningMessage('Generating report...');

    const warmupTimer = setTimeout(() => {
      setRunningMessage('Reading TSM Items data from DynamoDB...');
    }, 5000);

    const MAX_ATTEMPTS = 2;
    let lastError: unknown;

    try {
      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        try {
          const response = await runWholesaleComparison(
            report.id,
            ELIGIBLE_LOCATION_IDS,
            selectedYear,
            selectedMonth,
          );

          if (!response.success || !response.data) {
            throw new Error(response.error || 'Failed to generate report');
          }

          clearTimeout(warmupTimer);
          setReportData(response.data);
          setStatus('complete');
          return;
        } catch (err) {
          lastError = err;
          if (attempt < MAX_ATTEMPTS && isRetryableError(err)) {
            console.warn(`TSM Items attempt ${attempt} failed, retrying...`, err);
            setRunningMessage('Retrying...');
            continue;
          }
          throw err;
        }
      }
    } catch {
      clearTimeout(warmupTimer);
      setError(lastError instanceof Error ? lastError.message : 'Failed to generate report');
      setStatus('error');
    }
  };

  const handlePrintPDF = () => {
    printCurrentPage();
  };

  /**
   * Write one group's worksheet (Domestic / Import / Express / Unassigned).
   * Mirrors the convention from TsmGroupRankingReportModal — header rows
   * (1-3), NADA benchmark strip (4-5), metric leaders summary (6-7),
   * column headers (row 9), data rows from row 10.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const writeGroupSheet = (wb: any, data: TsmItemsReportData, section: TsmItemsGroupSection) => {
    const itemKeys = data.itemKeys;
    const compactHeaders: Array<{ key: ItemKey; label: string }> = itemKeys.map((k) => ({
      key: k,
      label: data.itemLabels[k],
    }));

    // 4 leading cols + 2 per item (count + pct) + 1 totalScore
    const totalCols = 4 + compactHeaders.length * 2 + 1;

    const sheetName = section.group; // ≤ 28 chars by construction
    const ws = wb.addWorksheet(sheetName, {
      views: [{ state: 'frozen', xSplit: 0, ySplit: 9 }],
      properties: { defaultRowHeight: 16 },
      pageSetup: {
        orientation: 'landscape',
        fitToPage: true,
        fitToWidth: 1,
        fitToHeight: 0,
      },
    });
    const lastColLetter = ws.getColumn(totalCols).letter;

    // Title rows
    ws.getCell('A1').value = `TSM Items — ${section.group}`;
    ws.getCell('A1').font = { size: 16, bold: true, color: { argb: 'FF1F2937' } };
    ws.mergeCells(`A1:${lastColLetter}1`);
    ws.getCell('A2').value = `${data.reportMonth.monthName} ${data.reportMonth.year} · ${section.totalAdvisors} advisor${section.totalAdvisors === 1 ? '' : 's'} ranked within ${section.group}`;
    ws.getCell('A2').font = { size: 11, color: { argb: 'FF4B5563' } };
    ws.mergeCells(`A2:${lastColLetter}2`);
    ws.getCell('A3').value = `Snapshot as of: ${data.asOf ? new Date(data.asOf).toLocaleString('en-US') : '—'}`;
    ws.getCell('A3').font = { size: 10, italic: true, color: { argb: 'FF6B7280' } };
    ws.mergeCells(`A3:${lastColLetter}3`);

    // NADA Benchmarks strip (single-line summary so we don't lose vertical space)
    ws.getCell('A4').value = 'NADA Benchmarks (industry-standard penetration % targets)';
    ws.getCell('A4').font = { size: 10, bold: true, color: { argb: 'FF1E3A8A' } };
    ws.getCell('A4').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEFF6FF' } };
    ws.mergeCells(`A4:${lastColLetter}4`);
    const benchmarkLine = compactHeaders
      .map(({ key, label }) => {
        const t = data.nadaBenchmarks[key];
        return `${label}: ${t === null ? '—' : `${t.toFixed(1)}%`}`;
      })
      .join(' · ');
    ws.getCell('A5').value = benchmarkLine;
    ws.getCell('A5').font = { size: 9, color: { argb: 'FF1E40AF' } };
    ws.getCell('A5').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEFF6FF' } };
    ws.getCell('A5').alignment = { wrapText: true, vertical: 'top' };
    ws.mergeCells(`A5:${lastColLetter}5`);

    // Metric leaders summary
    ws.getCell('A6').value = 'Metric Leaders (within group)';
    ws.getCell('A6').font = { size: 11, bold: true, color: { argb: 'FF1F2937' } };
    ws.getCell('A6').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3F4F6' } };
    ws.mergeCells(`A6:${lastColLetter}6`);
    const leaderLine = section.metricLeaders
      .map((m) => {
        const winners = m.winners.map((w) => w.advisorName).join(', ');
        const val = m.winners[0]
          ? `${m.winners[0].count} (${m.winners[0].pct.toFixed(1)}%)`
          : '—';
        return `${m.label}: ${winners || '—'} (${val})`;
      })
      .join(' · ');
    ws.getCell('A7').value = leaderLine || '—';
    ws.getCell('A7').font = { size: 9, color: { argb: 'FF4B5563' } };
    ws.getCell('A7').alignment = { wrapText: true, vertical: 'top' };
    ws.mergeCells(`A7:${lastColLetter}7`);
    ws.getRow(7).height = 50;

    ws.getCell('A8').value = 'Ranking — click the dropdown arrows to sort or filter';
    ws.getCell('A8').font = { size: 10, italic: true, color: { argb: 'FF6B7280' } };
    ws.mergeCells(`A8:${lastColLetter}8`);

    // Column header row (row 9)
    const headerRow = ws.getRow(9);
    const headers: string[] = ['#', 'Advisor', 'Location', 'Total ROs'];
    for (const { label } of compactHeaders) {
      headers.push(`${label} #`);
      headers.push(`${label} %`);
    }
    headers.push('Total Score');
    headers.forEach((h, i) => {
      const cell = headerRow.getCell(i + 1);
      cell.value = h;
      cell.font = { bold: true, size: 10, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF374151' } };
      cell.alignment = { horizontal: i <= 2 ? 'left' : 'right', vertical: 'middle' };
      cell.border = {
        top: { style: 'thin', color: { argb: 'FFD1D5DB' } },
        bottom: { style: 'medium', color: { argb: 'FF111827' } },
        left: { style: 'thin', color: { argb: 'FFD1D5DB' } },
        right: { style: 'thin', color: { argb: 'FFD1D5DB' } },
      };
    });
    headerRow.height = 22;

    const total = section.rows.length;
    const q1 = Math.max(1, Math.ceil(total * 0.25));
    const q4 = Math.floor(total * 0.75);
    const rankFill = (rank: number) => {
      if (rank <= q1) return 'FFD1FAE5';
      if (rank > q4) return 'FFFFE4E6';
      return 'FFF3F4F6';
    };
    const rankFontColor = (rank: number) => {
      if (rank <= q1) return 'FF065F46';
      if (rank > q4) return 'FF9F1239';
      return 'FF374151';
    };

    section.rows.forEach((row, i) => {
      const r = ws.getRow(10 + i);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const values: any[] = [i + 1, row.advisorName, row.locationName, row.totalROs];
      for (const { key } of compactHeaders) {
        values.push(row.itemCounts[key]);
        values.push(row.itemPcts[key] / 100);
      }
      values.push(row.totalScore);
      r.values = values;

      r.getCell(1).alignment = { horizontal: 'right' };
      r.getCell(1).font = { bold: true };
      r.getCell(2).alignment = { horizontal: 'left' };
      r.getCell(3).alignment = { horizontal: 'left' };
      for (let c = 4; c <= totalCols; c++) {
        r.getCell(c).alignment = { horizontal: 'right' };
      }

      // Percent formatting + rank-style color on count cell
      compactHeaders.forEach(({ key }, idx) => {
        const countCol = 5 + idx * 2;
        const pctCol = 6 + idx * 2;
        r.getCell(pctCol).numFmt = '0.0%';
        const rank = row.itemRanks[key];
        const fill = rankFill(rank);
        const font = rankFontColor(rank);
        r.getCell(countCol).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fill } };
        r.getCell(countCol).font = { bold: true, color: { argb: font } };
        r.getCell(pctCol).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fill } };
        r.getCell(pctCol).font = { color: { argb: font } };
      });

      // Total Score
      r.getCell(totalCols).font = { bold: true };
      r.getCell(totalCols).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF9FAFB' } };

      if (i % 2 === 1) {
        for (let c = 1; c <= totalCols; c++) {
          // Don't overwrite rank/score fills
          const itemColIdx = c - 5;
          const isRankCell = itemColIdx >= 0 && itemColIdx < compactHeaders.length * 2;
          if (isRankCell || c === totalCols) continue;
          r.getCell(c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFAFAFA' } };
        }
      }
    });

    if (section.rows.length > 0) {
      const lastRow = 9 + section.rows.length;
      ws.autoFilter = {
        from: { row: 9, column: 1 },
        to: { row: lastRow, column: totalCols },
      };
    }

    // Column widths
    ws.getColumn(1).width = 5;
    ws.getColumn(2).width = 24;
    ws.getColumn(3).width = 32;
    ws.getColumn(4).width = 9;
    compactHeaders.forEach((_, idx) => {
      ws.getColumn(5 + idx * 2).width = 8;
      ws.getColumn(6 + idx * 2).width = 9;
    });
    ws.getColumn(totalCols).width = 11;

    return ws;
  };

  const handleExportExcel = async () => {
    if (!reportData?.data) return;
    const data = reportData.data as TsmItemsReportData;

    const ExcelJS = await loadExcelJS();
    const wb = new ExcelJS.Workbook();
    wb.creator = "Darling's Dashboard";
    wb.created = new Date();

    const itemKeys = data.itemKeys;
    const compactHeaders: Array<{ key: ItemKey; label: string }> = itemKeys.map((k) => ({
      key: k,
      label: data.itemLabels[k],
    }));

    // Four group worksheets (Domestic first, Unassigned last).
    writeGroupSheet(wb, data, data.groups.domestic);
    writeGroupSheet(wb, data, data.groups.import);
    writeGroupSheet(wb, data, data.groups.express);
    writeGroupSheet(wb, data, data.groups.unassigned);

    // Multi-location fill-in footnote goes on the Domestic sheet (the first
    // sheet someone reaches in the workbook) — same convention as the on-screen
    // report which renders it once at the bottom of the page.
    if (data.multiLocationExcluded && data.multiLocationExcluded.length > 0) {
      const wsDom = wb.getWorksheet('Domestic');
      if (wsDom) {
        const startRow = 10 + data.groups.domestic.rows.length + 2;
        const totalCols = 4 + compactHeaders.length * 2 + 1;
        const lastColLetter = wsDom.getColumn(totalCols).letter;
        wsDom.getCell(`A${startRow}`).value = 'Excluded — multi-location fill-in advisors:';
        wsDom.getCell(`A${startRow}`).font = { bold: true, size: 9, color: { argb: 'FF4B5563' } };
        wsDom.mergeCells(`A${startRow}:${lastColLetter}${startRow}`);
        data.multiLocationExcluded.forEach((a, idx) => {
          const c = wsDom.getCell(`A${startRow + 1 + idx}`);
          c.value = `  • ${a.advisorName} (covered ${a.locations.join(', ')})`;
          c.font = { size: 9, italic: true, color: { argb: 'FF6B7280' } };
          wsDom.mergeCells(`A${startRow + 1 + idx}:${lastColLetter}${startRow + 1 + idx}`);
        });
      }
    }

    // Second sheet: By Location aggregate
    if (data.locationRows && data.locationRows.length > 0) {
      const totalLocCols = 3 + compactHeaders.length * 2;
      const wsLoc = wb.addWorksheet('By Location', {
        views: [{ state: 'frozen', xSplit: 0, ySplit: 3 }],
        pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0 },
      });
      const lastLocCol = wsLoc.getColumn(totalLocCols).letter;

      wsLoc.getCell('A1').value = `TSM Items by Location — ${data.reportMonth.monthName} ${data.reportMonth.year}`;
      wsLoc.getCell('A1').font = { size: 14, bold: true, color: { argb: 'FF1F2937' } };
      wsLoc.mergeCells(`A1:${lastLocCol}1`);

      const locHeaders: string[] = ['Location', 'Advisors', 'Total ROs'];
      for (const { label } of compactHeaders) {
        locHeaders.push(`${label} #`);
        locHeaders.push(`${label} %`);
      }
      const locHeaderRow = wsLoc.getRow(3);
      locHeaders.forEach((h, i) => {
        const cell = locHeaderRow.getCell(i + 1);
        cell.value = h;
        cell.font = { bold: true, size: 10, color: { argb: 'FFFFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF374151' } };
        cell.alignment = { horizontal: i === 0 ? 'left' : 'right', vertical: 'middle' };
      });
      locHeaderRow.height = 20;

      data.locationRows.forEach((lr, i) => {
        const r = wsLoc.getRow(4 + i);
        const values: any[] = [lr.locationName, lr.advisorCount, lr.totalROs];
        for (const { key } of compactHeaders) {
          values.push(lr.itemCounts[key]);
          values.push(lr.itemPcts[key] / 100);
        }
        r.values = values;
        r.getCell(1).alignment = { horizontal: 'left' };
        for (let c = 2; c <= totalLocCols; c++) r.getCell(c).alignment = { horizontal: 'right' };
        compactHeaders.forEach((_, idx) => {
          const pctCol = 5 + idx * 2;
          r.getCell(pctCol).numFmt = '0.0%';
        });
      });

      wsLoc.getColumn(1).width = 34;
      wsLoc.getColumn(2).width = 9;
      wsLoc.getColumn(3).width = 9;
      compactHeaders.forEach((_, idx) => {
        wsLoc.getColumn(4 + idx * 2).width = 8;
        wsLoc.getColumn(5 + idx * 2).width = 9;
      });
    }

    const buf = await wb.xlsx.writeBuffer();
    const blob = new Blob([buf], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `TSM-Items-${MONTHS[selectedMonth - 1]}-${selectedYear}.xlsx`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleClose = () => {
    setStatus('idle');
    setError(null);
    setReportData(null);
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) handleClose();
  };

  if (!isOpen) return null;

  const hasResults = status === 'complete';

  return (
    <div
      className="fixed inset-0 z-50 overflow-hidden flex items-center justify-center p-4 print:p-0 print:overflow-visible print:static"
      onClick={handleBackdropClick}
    >
      <div className="absolute inset-0 bg-black bg-opacity-50 transition-opacity print:hidden" />
      <div className="relative max-w-7xl w-full max-h-[95vh] bg-white rounded-lg shadow-xl flex flex-col print:max-w-none print:max-h-none print:rounded-none print:shadow-none">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between print:border-b-2">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{report.title}</h2>
            <p className="text-sm text-gray-500 mt-1">
              Per-TSM penetration % of 7 tracked labor-op items — printable for posting
            </p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 print:hidden"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 print:overflow-visible">
          <div className="mb-6 print:hidden">
            <div className="flex flex-wrap items-end gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Period</label>
                <div className="flex gap-2">
                  <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(parseInt(e.target.value, 10))}
                    disabled={status === 'running'}
                    className="block rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 text-sm"
                  >
                    {MONTHS.map((name, i) => (
                      <option key={i + 1} value={i + 1}>{name}</option>
                    ))}
                  </select>
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
                    disabled={status === 'running'}
                    className="block rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 text-sm"
                  >
                    {yearOptions.map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
              </div>

              <button
                onClick={handleRunReport}
                disabled={status === 'running'}
                className={`
                  inline-flex items-center px-4 py-2 text-sm font-medium rounded-md transition-colors
                  ${status === 'running'
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-orange-600 text-white hover:bg-orange-700'
                  }
                `}
              >
                {status === 'running' ? (
                  <>
                    <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    {runningMessage}
                  </>
                ) : (
                  <>
                    <PlayIcon className="h-4 w-4 mr-2" />
                    Run Report
                  </>
                )}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Ranks all Service Advisors across the 8 service-bearing locations (excludes Loc 12 &amp; 44 — Body Shop).
              Location dropdown on the page is ignored — this report is always company-wide.
            </p>
          </div>

          <div className="border-t border-gray-200 mb-6 print:hidden" />

          <div id="report-print-content">
            <div className="hidden print:block mb-4">
              <h1 className="text-xl font-bold">TSM Items Report</h1>
              <p className="text-sm text-gray-600">
                {MONTHS[selectedMonth - 1]} {selectedYear}
              </p>
            </div>

            {status === 'idle' && (
              <div className="text-center py-12">
                <DocumentChartBarIcon className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500">
                  Pick a month and click "Run Report" to generate the company-wide TSM Items leaderboard.
                </p>
              </div>
            )}

            {status === 'running' && (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-orange-600 mb-4" />
                <p className="text-gray-500">{runningMessage}</p>
              </div>
            )}

            {status === 'error' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-700">{error || 'An error occurred while generating the report.'}</p>
                <button
                  onClick={() => setStatus('idle')}
                  className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
                >
                  Try again
                </button>
              </div>
            )}

            {status === 'complete' && reportData && (
              <TsmItemsReport data={reportData.data as TsmItemsReportData} />
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between print:hidden">
          <ReportExportButtons
            onPrintPDF={handlePrintPDF}
            onExportExcel={handleExportExcel}
            disabled={!hasResults}
          />
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
